import { useMatches, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { errorCode } from '@/api/errors';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * Hook that checks if the user is authenticated and redirects to login if not.
 * Use this in components that require authentication.
 */
export function useAuthCheck(): void {
  const navigate = useNavigate();
  const matches = useMatches();
  const { me: user, isSuccess, failureReason } = useCurrentUser();

  // Check if we're on the login page
  const isLoginPage = matches.some((match) => match.routeId === '/_auth/login');
  const isRegisterPage = matches.some(
    (match) => match.routeId === '/_auth/register',
  );
  // EXACT ROUTE-ID EQUALITY, not a prefix match, so every public page needs its
  // own entry here or an anonymous visitor is bounced to /login the moment
  // `getCurrentUser` 401s.
  const isPublicPage = matches.some(
    (match) =>
      match.routeId === '/study-helper-application' ||
      // GF 12 and GF 14. Both intake forms are anonymous in legacy —
      // `display_meta` carries `requireLogin=false`, there is no nonce and no
      // entry limit, and an anonymous crawl of staging renders both — and
      // approving one is the only path to a `FollowupCoordinator` or
      // `FollowupPartner` account.
      match.routeId === '/follow-up-coordinator-application' ||
      match.routeId === '/follow-up-partner-application' ||
      // Reached by redirect from the login form when the server answers
      // `password_reset_required`, so the visitor is by definition signed out.
      // Without this the 401 from `getCurrentUser` bounces them straight back
      // to /login/ and the redirect looks like the form did nothing.
      match.routeId === '/_auth/first-time-password-reset',
  );

  const error_code = errorCode(failureReason);

  useEffect(() => {
    // Redirect unauthenticated users to login
    if (
      !isLoginPage &&
      !isRegisterPage &&
      !isPublicPage &&
      error_code === 'invalid_auth_token'
    ) {
      // Redirect to login page
      navigate({ to: '/login/' });
    }

    // Redirect authenticated users away from login page
    if ((isLoginPage || isRegisterPage) && isSuccess && user) {
      navigate({ to: '/' });
    }
  }, [
    error_code,
    navigate,
    isLoginPage,
    isRegisterPage,
    isPublicPage,
    isSuccess,
    user,
  ]);
}
